import {
  AgreeToTermsPolicy,
  experience,
  ExtraParamsKey,
  SignInIdentifier,
  SignInMode,
} from '@logto/schemas';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useSearchParams } from 'react-router-dom';

import LandingPageLayout from '@/Layout/LandingPageLayout';
import SingleSignOnFormModeContextProvider from '@/Providers/SingleSignOnFormModeContextProvider';
import WebAuthnContextProvider from '@/Providers/WebAuthnContextProvider';
import PasskeySignInButton from '@/components/Button/PasskeySignInButton';
import GoogleOneTap from '@/components/GoogleOneTap';
import TextLink from '@/components/TextLink';
import SocialSignInList from '@/containers/SocialSignInList';
import TermsAndPrivacyCheckbox from '@/containers/TermsAndPrivacyCheckbox';
import TermsAndPrivacyLinks from '@/containers/TermsAndPrivacyLinks';
import useNavigateWithPreservedSearchParams from '@/hooks/use-navigate-with-preserved-search-params';
import usePrefilledIdentifier from '@/hooks/use-prefilled-identifier';
import { useSieMethods } from '@/hooks/use-sie';
import useTerms from '@/hooks/use-terms';
import { usePlatformCopy } from '@/shared/platform/copy';

import ErrorPage from '../ErrorPage';

import Main from './Main';
import styles from './index.module.scss';

const EntryIcon = ({ employee = false }: { readonly employee?: boolean }) => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    {employee ? (
      <path d="M4 21V5l10-3v19M14 9h6v12M2 21h20M8 7h2M8 11h2M8 15h2M17 13h1M17 17h1" />
    ) : (
      <>
        <rect x="3" y="5" width="18" height="14" rx="3" />
        <path d="m4 7 8 6 8-6" />
      </>
    )}
  </svg>
);
const Chevron = () => (
  <svg className="platform-disclosure-chevron" viewBox="0 0 24 24" aria-hidden="true">
    <path d="m8 10 4 4 4-4" />
  </svg>
);

const SignInOptions = () => {
  const copy = usePlatformCopy();
  const { t } = useTranslation();
  const {
    signInMethods,
    signUpMethods,
    socialConnectors,
    signInMode,
    singleSignOnEnabled,
    passkeySignIn,
  } = useSieMethods();
  const { termsValidation, agreeToTermsPolicy } = useTerms();
  const navigate = useNavigateWithPreservedSearchParams();
  const prefilled = usePrefilledIdentifier({
    enabledIdentifiers: signInMethods.map(({ identifier }) => identifier),
  });
  // Re-entering an email flow must reveal its retained identifier. Without social
  // sign-in, show the form directly; the secondary employee entry does not block it.
  const [expanded, setExpanded] = useState<'email' | 'employee' | undefined>(() =>
    signInMethods.length > 0 && (prefilled.value || socialConnectors.length === 0)
      ? 'email'
      : undefined,
  );
  const [isSsoPending, setIsSsoPending] = useState(false);
  const [ssoError, setSsoError] = useState(false);
  const providers = [...socialConnectors].sort((a, b) => {
    const order = (target: string) => (target === 'google' ? 0 : target === 'github' ? 1 : 2);
    return order(a.target) - order(b.target);
  });
  const handleSsoNavigation = useCallback(async () => {
    if (isSsoPending) return;
    setIsSsoPending(true);
    setSsoError(false);
    try {
      if (agreeToTermsPolicy === AgreeToTermsPolicy.Manual && !(await termsValidation())) return;
      navigate('/single-sign-on/email');
    } catch {
      setSsoError(true);
    } finally {
      setIsSsoPending(false);
    }
  }, [agreeToTermsPolicy, isSsoPending, navigate, termsValidation]);

  return (
    <div className="platform-login-options">
      {providers.length > 0 && (
        <SocialSignInList socialConnectors={providers} className="platform-social-primary" />
      )}
      {providers.length > 0 && (signInMethods.length > 0 || singleSignOnEnabled) && (
        <div className="platform-method-divider">
          <span>{copy.otherMethods}</span>
        </div>
      )}
      {signInMethods.length > 0 && (
        <section className="platform-login-disclosure">
          <button
            id="platform-email-toggle"
            type="button"
            className="platform-disclosure-toggle"
            aria-expanded={expanded === 'email'}
            aria-controls="platform-email-panel"
            onClick={() => setExpanded(expanded === 'email' ? undefined : 'email')}
          >
            <EntryIcon />
            <span>
              {signInMethods.some(({ identifier }) => identifier === SignInIdentifier.Email)
                ? copy.emailEntry
                : copy.identifierEntry}
            </span>
            <Chevron />
          </button>
          <div
            id="platform-email-panel"
            role="region"
            aria-labelledby="platform-email-toggle"
            hidden={expanded !== 'email'}
            className="platform-disclosure-panel platform-email-panel"
          >
            <Main signInMethods={signInMethods} socialConnectors={[]} />
          </div>
        </section>
      )}
      {agreeToTermsPolicy === AgreeToTermsPolicy.Manual && expanded !== 'email' && (
        <TermsAndPrivacyCheckbox className="platform-login-terms" />
      )}
      {passkeySignIn?.enabled && passkeySignIn.showPasskeyButton && <PasskeySignInButton />}
      {signInMode === SignInMode.SignInAndRegister && signUpMethods.length > 0 && (
        <div className="platform-create-account">
          {t('description.no_account')}{' '}
          <TextLink replace to="/register">
            {copy.create}
          </TextLink>
        </div>
      )}
      {singleSignOnEnabled && (
        <section className="platform-employee-secondary">
          <button
            id="platform-employee-toggle"
            type="button"
            className="platform-employee-link"
            aria-expanded={expanded === 'employee'}
            aria-controls="platform-employee-panel"
            onClick={() => setExpanded(expanded === 'employee' ? undefined : 'employee')}
          >
            <EntryIcon employee />
            <span>{copy.employeeEntry}</span>
            <Chevron />
          </button>
          <div
            id="platform-employee-panel"
            role="region"
            aria-labelledby="platform-employee-toggle"
            hidden={expanded !== 'employee'}
            className="platform-disclosure-panel"
          >
            <p className="platform-employee-hint">{copy.employeeHint}</p>
            <button
              className="platform-employee-button"
              type="button"
              disabled={isSsoPending}
              onClick={() => {
                void handleSsoNavigation();
              }}
            >
              {isSsoPending ? copy.continuing : copy.employee}
            </button>
            {ssoError && <p role="alert">{copy.updateError}</p>}
          </div>
        </section>
      )}
    </div>
  );
};

const SignIn = () => {
  const { signInMode } = useSieMethods();
  const { agreeToTermsPolicy } = useTerms();
  const [params] = useSearchParams();
  if (!signInMode) return <ErrorPage />;
  if (signInMode === SignInMode.Register) return <Navigate to="/register" />;
  if (params.get(ExtraParamsKey.OneTimeToken))
    return (
      <Navigate
        replace
        to={{ pathname: `/${experience.routes.oneTimeToken}`, search: `?${params.toString()}` }}
      />
    );
  return (
    <LandingPageLayout title="description.sign_in_to_your_account">
      <GoogleOneTap context="signin" />
      <WebAuthnContextProvider>
        <SingleSignOnFormModeContextProvider>
          <SignInOptions />
        </SingleSignOnFormModeContextProvider>
      </WebAuthnContextProvider>
      {agreeToTermsPolicy !== AgreeToTermsPolicy.Manual && (
        <TermsAndPrivacyLinks className={styles.terms} />
      )}
    </LandingPageLayout>
  );
};
export default SignIn;
